/**
 * startImpersonation orchestration.
 *
 * Phase 3.1 Plan 03 (per ADR-006 section 3 + ADR-005):
 *   Swap the active staff token pair for an impersonation pair so the SPA
 *   re-registers under the client route surface. The token-swap dance is:
 *
 *     1. Read the current access + refresh tokens (staff pair).
 *     2. POST /v1/admin/impersonate/<org_id> with the staff bearer.
 *     3. On 200: save the staff pair to staffSidecar.
 *     4. Replace the active tokens with the impersonation access token AND
 *        the IMPERSONATION_NO_REFRESH sentinel for the active refresh slot.
 *        The sentinel is what api-client refresh-on-401 detects to trigger
 *        the graceful access-expiry recovery (B2 mitigation; replaces the
 *        silent-corruption failure mode where staff refresh would swap back
 *        and leave React state out of sync).
 *     5. Refetch /v1/auth/me to get the new claims (impersonating_as=true,
 *        target_org populated).
 *     6. Return the new User; the caller (AuthProvider.startImpersonation
 *        context method) sets the React state which causes router rebuild.
 *
 *   Failure handling:
 *     - Pre-call guard: rejects if no staff session is active or if the
 *       active refresh slot already holds the IMPERSONATION_NO_REFRESH
 *       sentinel (a double-start would corrupt the sidecar).
 *     - On /v1/admin/impersonate/<org_id> non-200: re-throws the error from
 *       startImpersonationRequest; the sidecar is untouched and the active
 *       tokens remain the staff pair.
 *     - On /v1/auth/me failure post-swap: reverses the swap (restores staff
 *       to active, clears sidecar) and re-throws.
 */
import { authStorage, staffSidecar, IMPERSONATION_NO_REFRESH } from '@/lib/auth-storage';
import { meRequest } from '@/features/auth/api';
import { startImpersonationRequest } from './api';
import { StartImpersonationError } from './schemas';
import type { User } from '@/features/auth/schemas';

/**
 * Swap active staff tokens for an impersonation pair targeting the given
 * client org, then return the post-swap User from /v1/auth/me.
 *
 * @param orgId - The target client org ID to view as
 * @returns The post-swap User with impersonating_as=true + target_org set
 * @throws StartImpersonationError if the swap or /me probe fails
 */
export async function startImpersonation(orgId: string): Promise<User> {
  const staffAccess = authStorage.getAccess();
  const staffRefresh = authStorage.getRefresh();

  // Guard: no staff session active.
  if (!staffAccess || !staffRefresh) {
    throw new StartImpersonationError(
      'no_active_staff_session',
      0,
      'Cannot start impersonation: no active staff session',
    );
  }

  // Guard: already impersonating (sentinel in active refresh slot). A
  // double-start would overwrite the existing sidecar with the
  // impersonation pair, losing the original staff tokens forever.
  if (staffRefresh === IMPERSONATION_NO_REFRESH) {
    throw new StartImpersonationError(
      'already_impersonating',
      0,
      'Cannot start impersonation: already impersonating',
    );
  }

  // Step 1: hit the start endpoint. Throws StartImpersonationError on non-200;
  // the active tokens are still the staff pair so the caller can recover.
  const start = await startImpersonationRequest(orgId);

  // Step 2: save the staff pair to sidecar BEFORE swapping. If the next
  // authStorage.set throws (extremely unlikely but possible if localStorage
  // quota is exhausted) we still have the staff pair in the sidecar to
  // recover from.
  staffSidecar.set(staffAccess, staffRefresh);

  // Step 3: replace active tokens. The impersonation response carries no
  // refresh_token (per ADR-005 1-hour cap); we put the IMPERSONATION_NO_REFRESH
  // sentinel in the active refresh slot. api-client.ts checks for this
  // sentinel before calling /v1/auth/refresh and routes to the graceful
  // access-expiry path instead.
  authStorage.set(start.access_token, IMPERSONATION_NO_REFRESH);

  // Step 4: refetch /me to pick up the new impersonation claims. On
  // failure reverse the swap (restore staff to active, clear sidecar) so
  // the user is not stranded.
  try {
    const me = await meRequest();
    return me;
  } catch (err) {
    authStorage.set(staffAccess, staffRefresh);
    staffSidecar.clear();
    throw new StartImpersonationError(
      'me_probe_failed',
      0,
      err instanceof Error ? err.message : 'me_probe_failed',
    );
  }
}
